import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import { MAIL_CATEGORIES } from 'modules/Mails/types';
import { LightDebtorSchema } from 'shared/hooks/use-load-light-debtors';
import { LightUserSchema } from 'shared/hooks/use-load-light-users';
import { addResourceNameToQueryResult } from 'shared/hooks/utils/add-resource-name-to-query-result';
import { useAxiosInstance } from 'shared/hooks/utils/axios-instance';
import { handleZodError } from 'shared/hooks/utils/handle-zod-error';
import type { ApiError } from 'shared/hooks/utils/types';
import { z } from 'zod/v4';

export const MailSchema = z.object({
  id: z.coerce.string(),
  date: z.string().transform(dayjs).nullable(),
  subject: z.string().nullable(),
  fromMail: z.string(),
  toMail: z.array(z.email()),
  cc: z.array(z.email()).nullable(),
  bcc: z.array(z.email()).nullable(),
  read: z.boolean(),
  fromName: z.string().nullable(),
  hasAttachments: z.boolean(),
  body: z.string().nullable(),
  isDraft: z.boolean(),
  categories: z.array(z.enum([...MAIL_CATEGORIES])),
  attachments: z.array(
    z.object({
      filename: z.string(),
      url: z.url(),
    }),
  ),
});

const ThreadSchema = z.object({
  id: z.coerce.string(),
  accountManager: LightUserSchema.nullable(),
  debtor: LightDebtorSchema.nullable(),
  isProcessed: z.boolean(),
  mails: z.array(MailSchema),
});

export type Thread = z.infer<typeof ThreadSchema>;
export type Mail = z.infer<typeof MailSchema>;

async function loadMailFn(axiosInstance: Promise<AxiosInstance>, { queryKey }: any) {
  const instance = await axiosInstance;

  const { data } = await instance.get(`/mails/threads/${queryKey[1]}`);

  const parseResult = ThreadSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'thread');
    throw new SilentError();
  }

  return ThreadSchema.parse(data);
}

const EMPTY_THREAD: Thread = {
  id: '',
  accountManager: null,
  debtor: null,
  isProcessed: false,
  mails: [],
};

export function useLoadMail(mailId: string) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<Thread, ApiError>({
    queryKey: ['mail', mailId],
    queryFn: context => loadMailFn(axiosInstance, context),
    placeholderData: previousData => previousData ?? EMPTY_THREAD,
  });

  const result = addResourceNameToQueryResult<Thread, ApiError, 'thread'>('thread', queryResult);

  // placeholderData only applies while the query is pending: if the request fails before ever
  // succeeding, `thread` becomes undefined even though the MailDetail components (thread.debtor,
  // thread.isProcessed, getLastMail(thread), ...) access it unguarded and would crash on render.
  // The fallback keeps `thread` defined in that error state — the failure itself stays observable
  // via threadError.
  return { ...result, thread: result.thread ?? EMPTY_THREAD };
}
